import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Button — the only button primitive. Compose everything from variants and
 * sizes; never restyle a raw `<button>` in a screen.
 *
 * Props:
 *   variant  'primary' (brand fill) | 'secondary' (neutral fill) |
 *            'outline' | 'ghost' | 'destructive' | 'link'
 *   size     'sm' (h-8) | 'default' (h-9) | 'lg' (h-10) | 'touch' (h-11 —
 *            the 44px tier for mobile-first forms such as auth) |
 *            'icon' (h-9 w-9) | 'icon-sm' (h-8 w-8, via IconButton) |
 *            'icon-xs' (h-6 w-6 — the
 *            24px floor for dense inline affordances such as tree-disclosure
 *            chevrons; use IconButton so the accessible name is mandatory)
 *   loading  shows a spinner, sets `aria-busy` and disables the button while
 *            keeping the label announced to screen readers.
 *   asChild  render the variants onto the single child element instead of a
 *            `<button>` (e.g. `<Button asChild><Link …/></Button>`), so
 *            navigation stays a real anchor.
 *
 * Focus: keyboard focus uses the global token ring (see index.css); this
 * component intentionally defines no per-instance focus style.
 */
export const buttonVariants = cva(
  [
    "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium",
    "transition-colors duration-color ease-standard",
    "disabled:pointer-events-none disabled:opacity-60",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-core text-on-core shadow-control hover:bg-core-hover active:bg-core-hover",
        secondary:
          "bg-bg-300 text-ink-100 hover:bg-line-100 active:bg-line-100",
        outline:
          "border border-line-200 bg-bg-200 text-ink-100 shadow-control hover:bg-bg-400 active:bg-bg-400",
        ghost:
          "text-ink-200 hover:bg-bg-400 hover:text-ink-100 active:bg-bg-400",
        destructive:
          "border border-status-blocked bg-transparent text-status-blocked shadow-none hover:bg-status-blocked-soft hover:text-status-blocked active:bg-status-blocked-soft",
        link: "text-core underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-sm px-3 text-xs",
        lg: "h-10 rounded-sm px-6",
        touch: "h-11 rounded-sm px-5",
        icon: "h-9 w-9",
        "icon-xs": "h-6 w-6 rounded-sm",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={asChild ? undefined : (disabled ?? loading)}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 aria-hidden="true" className="animate-spin" />
            <span>{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button };
